import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {useTranslation} from 'react-i18next';
import {TransactionalMailTemplate} from 'crqlar-react-common/dist/models/newsletter/transactionalMailTemplate.model';
import {TransactionalMailTemplateService} from 'crqlar-react-common/dist/services/newsletter/transactionalMailTemplate.service';
import ContentEditor from 'crqlar-react-common/dist/components/ContentEditor.component';
import type {BuilderMergeTags} from 'crqlar-react-common/dist/components/ContentEditor.component';
import UnsavedChangesDialog from 'crqlar-react-common/dist/components/UnsavedChangesDialog';
import {ContentSource} from 'crqlar-react-common/dist/models/content/content.model';
import {LoadingButton} from '@mui/lab';
import {CustomDomainService} from 'crqlar-react-common/dist/services/newsletter/customDomain.service';
import {CustomDomainSetupResponse} from 'crqlar-react-common/dist/models/newsletter/customDomain.model';
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    templateToEdit?: TransactionalMailTemplate;
}

const PLACEHOLDER_GROUPS = [
    {
        label: 'Guest',
        placeholders: [
            {key: 'firstName', label: 'First Name'},
            {key: 'lastName', label: 'Last Name'},
            {key: 'fullName', label: 'Full Name'},
            // {key: 'namePrefix', label: 'Name Prefix'},
            // {key: 'nameTitle', label: 'Name Title'},
            {key: 'email', label: 'Email'},
            {key: 'phone', label: 'Phone'},
            {key: 'salutation', label: 'Salutation'},
            // {key: 'jobTitle', label: 'Job Title'},
            // {key: 'addressGreetings', label: 'Address Greetings'},
            // {key: 'letterGreetings', label: 'Letter Greetings'},
        ],
    },
    {
        label: 'Hotel Information',
        placeholders: [
            {key: 'companyLogo', label: 'Hotel Logo'},
            // {key: 'streetAddress', label: 'Street Address'},
            {key: 'zipCode', label: 'Zip Code'},
            {key: 'city', label: 'City'},
            {key: 'country', label: 'Country'},
            {key: 'directionsURL', label: 'Google Maps Link'},
        ],
    },
    {
        label: 'Reservation',
        placeholders: [
            {key: 'confirmationCode', label: 'Confirmation Code'},
            {key: 'serviceName', label: 'Service Name'},
            {key: 'serviceDuration', label: 'Service Duration'},
            {key: 'serviceThumbnail', label: 'Service Thumbnail'},
            {key: 'servicePrice', label: 'Service Price'},
            {key: 'dateAndTime', label: 'Date & Time'},
            {key: 'date', label: 'Date'},
            {key: 'time', label: 'Time'},
            {key: 'guestNumber', label: 'Guest Number'},
            {key: 'guestNote', label: 'Widget Reservation Note by Guest'},
            {key: 'facilities', label: 'Facilities'},
            {key: 'staff', label: 'Staff'},
        ],
    },
];

const parseEmailList = (value: string): string[] =>
    value.split(',').map(entry => entry.trim()).filter(entry => !!entry);

const CreateTransactionalMailTemplateDialog: React.FC<Props> = ({open, onClose, onSaved, templateToEdit}) => {
    const {t} = useTranslation();
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const readOnlyUser = useReadOnly()

    // The same placeholders as the copy chips, offered in the builder's text toolbar.
    const mergeTags = useMemo<BuilderMergeTags>(() => Object.fromEntries(PLACEHOLDER_GROUPS.map(group => [
        group.label,
        {
            name: t(group.label),
            mergeTags: Object.fromEntries(group.placeholders.map(p => [p.key, {name: t(p.label), value: `{{${p.key}}}`}]))
        }
    ])), [t]);

    const handleCopyPlaceholder = (key: string) => {
        navigator.clipboard.writeText(`{{${key}}}`);
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 1500);
    };

    const [name, setName] = useState(templateToEdit?.name ?? '');
    const [fromName, setFromName] = useState(templateToEdit?.fromName ?? '');
    const [emailPrefix, setEmailPrefix] = useState((templateToEdit?.fromEmail ?? '').split('@')[0] || 'noreply');
    const [replyToEmail, setReplyToEmail] = useState(templateToEdit?.replyToEmail ?? '');
    const [bccEmails, setBccEmails] = useState(templateToEdit?.bccEmails ?? '');
    const [subject, setSubject] = useState(templateToEdit?.subject ?? '');
    const [htmlContent, setHtmlContent] = useState(templateToEdit?.htmlContent ?? '');
    const [unlayerDesign, setUnlayerDesign] = useState(templateToEdit?.unlayerDesign ?? '');
    const [saving, setSaving] = useState(false);
    const editorRef = useRef<ContentEditor>(null);
    const [contentChanged, setContentChanged] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [customDomain, setCustomDomain] = useState<CustomDomainSetupResponse | null>(null);

    useEffect(() => {
        if (open) {
            setName(templateToEdit?.name ?? '');
            setFromName(templateToEdit?.fromName ?? '');
            setEmailPrefix((templateToEdit?.fromEmail ?? '').split('@')[0] || 'noreply');
            setReplyToEmail(templateToEdit?.replyToEmail ?? '');
            setBccEmails(templateToEdit?.bccEmails ?? '');
            setSubject(templateToEdit?.subject ?? '');
            setHtmlContent(templateToEdit?.htmlContent ?? '');
            setUnlayerDesign(templateToEdit?.unlayerDesign ?? '');
            setError(null);
            setContentChanged(false);
            setConfirmClose(false);

            CustomDomainService.get()
                .then(domain => setCustomDomain(domain))
                .catch(() => setCustomDomain(null));
        }
    }, [open, templateToEdit]);

    // Timed HTML exports also run for untouched templates; only user design updates count.
    const hasUnsavedChanges = !readOnlyUser && (
        contentChanged
        || name !== (templateToEdit?.name ?? '')
        || fromName !== (templateToEdit?.fromName ?? '')
        || emailPrefix !== ((templateToEdit?.fromEmail ?? '').split('@')[0] || 'noreply')
        || replyToEmail !== (templateToEdit?.replyToEmail ?? '')
        || bccEmails !== (templateToEdit?.bccEmails ?? '')
        || subject !== (templateToEdit?.subject ?? '')
    );

    const requestClose = () => {
        if (saving) return;
        if (hasUnsavedChanges) {
            setConfirmClose(true);
        } else {
            onClose();
        }
    };

    const hasValidDomain = !!customDomain?.valid;
    const emailSuffix = hasValidDomain
        ? `@${customDomain!.subdomain}.${customDomain!.domain}`
        : '@crqlar.com';

    // SendGrid rejects a personalization whose recipient addresses are not all unique, so the
    // reply-to address must never be repeated in the BCC list and no BCC address may be listed
    // twice — a template saved that way would fail on every send.
    const normalizedReplyTo = replyToEmail.trim().toLowerCase();
    const normalizedBcc = parseEmailList(bccEmails).map(entry => entry.toLowerCase());
    const bccMatchesReplyTo = !!normalizedReplyTo && normalizedBcc.includes(normalizedReplyTo);
    const bccHasDuplicates = new Set(normalizedBcc).size !== normalizedBcc.length;
    const bccError = bccMatchesReplyTo
        ? t('BCC addresses must be different from the reply-to address')
        : bccHasDuplicates
            ? t('Each BCC address can only be listed once')
            : null;

    const handleSave = async () => {
        if (saving || readOnlyUser) return;
        if (!name.trim()) {
            setError(t('Template name is required'));
            return;
        }
        if (bccError) {
            setError(bccError);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (!editorRef.current) throw new Error('Content editor is not ready');
            const latestContent = await editorRef.current.exportContent();
            const payload: TransactionalMailTemplate = {
                ...templateToEdit,
                name: name.trim(),
                fromName: fromName.trim(),
                fromEmail: emailPrefix.trim() ? `${emailPrefix.trim()}${emailSuffix}` : '',
                replyToEmail: replyToEmail.trim(),
                bccEmails: bccEmails.trim() || undefined,
                subject: subject.trim(),
                htmlContent: latestContent.generatedHtmlCode ?? '',
                unlayerDesign: latestContent.content ?? '',
            };
            if (templateToEdit?.id) {
                await TransactionalMailTemplateService.update(payload);
            } else {
                await TransactionalMailTemplateService.create(payload);
            }
            onSaved();
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? t('Failed to save template. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleEditorSaved = (content?: ContentSource) => {
        if (content) {
            setHtmlContent(content.generatedHtmlCode ?? '');
            setUnlayerDesign(content.content ?? '');
        }
    };

    const isEditing = !!templateToEdit?.id;

    return (
        <>
            <Dialog open={open} onClose={requestClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{pb: 1}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            {isEditing ? t('Edit Email Template') : t('Create Email Template')}
                        </Typography>
                        <IconButton onClick={requestClose} disabled={saving} size="small" aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={2.5} sx={{mt: 1}}>
                        <TextField
                            size="small"
                            disabled={readOnlyUser || saving}
                            label={t('Template Name')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                            error={!!error && !name.trim()}
                            helperText={!name.trim() && error ? error : undefined}
                        />

                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                            <TextField
                                size="small"
                                disabled={readOnlyUser || saving}
                                label={t('From Name')}
                                value={fromName}
                                onChange={(e) => setFromName(e.target.value)}
                                fullWidth
                                placeholder={t('e.g. Hotel Reservation Team')}
                            />
                            <TextField
                                size="small"
                                disabled={readOnlyUser || saving}
                                label={t('Sender Email Address')}
                                value={emailPrefix}
                                onChange={(e) => setEmailPrefix(e.target.value.replace(/[@\s]/g, ''))}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography
                                                variant="body1"
                                                color="text.secondary"
                                                sx={{whiteSpace: 'nowrap', userSelect: 'none'}}
                                            >
                                                {emailSuffix}
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>

                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="flex-start">
                            <TextField
                                size="small"
                                disabled={readOnlyUser || saving}
                                label={t('Reply to Mail Address')}
                                value={replyToEmail}
                                onChange={(e) => setReplyToEmail(e.target.value)}
                                fullWidth
                                type="email"
                                placeholder={t('e.g. noreply@yourdomain.com')}
                                error={bccMatchesReplyTo}
                            />
                            <TextField
                                size="small"
                                disabled={readOnlyUser || saving}
                                label={t('BCC: Internal Mailing')}
                                value={bccEmails}
                                onChange={(e) => setBccEmails(e.target.value)}
                                fullWidth
                                placeholder={t('e.g. reception@hotel.com, manager@hotel.com')}
                                error={!!bccError}
                                helperText={bccError ?? t('Comma-separated email addresses that receive a BCC copy of every confirmation email')}
                            />
                        </Stack>

                        <TextField
                            size="small"
                            disabled={readOnlyUser || saving}
                            label={t('Subject Line')}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            fullWidth
                            // The example has to show real placeholder keys, so the {{…}} tags are
                            // passed in as interpolation values — i18next would otherwise read them
                            // as its own variables. {{name}} was neither: nothing ever fills it.
                            placeholder={t('e.g. Your reservation for {{serviceTag}} on {{dateTag}} is confirmed!', {
                                serviceTag: '{{serviceName}}',
                                dateTag: '{{date}}',
                            })}
                        />

                        <Divider/>

                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('Email Content')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('Use transaction tags to automatically pull real-time data into your emails. Deliver a seamless, professional experience from the moment of booking.')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{display: 'block', textTransform: 'uppercase', letterSpacing: 0.5}}>
                                {t('Available Placeholders — click to copy')}
                            </Typography>
                            <Stack spacing={0.75}>
                                {PLACEHOLDER_GROUPS.map(group => (
                                    <Stack key={group.label} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Typography variant="body2" color="text.secondary" sx={{minWidth: 100, fontWeight: 600}}>
                                            {t(group.label)}
                                        </Typography>
                                        {group.placeholders.map(p => (
                                            <Tooltip key={p.key} title={copiedKey === p.key ? t('Copied!') : `{{${p.key}}}`}>
                                                <Chip
                                                    key={p.key}
                                                    label={t(p.label)}
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<ContentCopyIcon sx={{fontSize: '0.85rem !important'}}/>}
                                                    onClick={() => handleCopyPlaceholder(p.key)}
                                                    color={copiedKey === p.key ? 'success' : 'default'}
                                                    sx={{cursor: 'pointer', fontSize: '0.8rem', height: 26}}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>

                            <Box
                                // Read-only users may view the template but must not modify it.
                                // The Unlayer editor has no read-only mode, so the whole editor
                                // surface is made inert + pointer-events:none (blocks clicking,
                                // dragging and typing into the editor iframe). Save is disabled too.
                                ref={(el: HTMLElement | null) => {
                                    if (el) el.inert = readOnlyUser || saving;
                                }}
                                sx={{
                                    height: 600,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    pointerEvents: readOnlyUser || saving ? 'none' : undefined,
                                    opacity: readOnlyUser || saving ? 0.6 : undefined,
                                }}
                            >
                                <ContentEditor
                                    ref={editorRef}
                                    content={{
                                        content: unlayerDesign,
                                        generatedHtmlCode: htmlContent,
                                    } as ContentSource}
                                    onSaved={handleEditorSaved}
                                    onChange={() => setContentChanged(true)}
                                    displayMode="email"
                                    mergeTags={mergeTags}
                                    mergeTagsLabel={t('Variables')}
                                />
                            </Box>
                        </Stack>

                        {error && name.trim() && !bccError && (
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={requestClose} disabled={saving}>
                        {t('Cancel')}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={saving}
                        onClick={handleSave}
                        disabled={!name.trim() || !!bccError || readOnlyUser}
                    >
                        {t('Save')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <UnsavedChangesDialog
                open={open && confirmClose}
                onCancel={() => setConfirmClose(false)}
                onConfirm={() => {
                    setConfirmClose(false);
                    onClose();
                }}
                cancelText="Keep editing"
                confirmText="Discard"
            />
        </>
    );
};

export default CreateTransactionalMailTemplateDialog;
